// /services/art-fabrication/ (ProjectServicePage). Was a Contentful service
// entry until 2026-11-04; gatsby-node now skips it.
// GSC Aug–Oct 2026: 26 clicks, 2.9K impressions, avg position 40. Queries:
// "art fabrication" (pos 51), "art fabrication companies" (pos 41), "art
// fabrication near me", "art fabrication nyc" (pos 16.5), "art fabricators".
// Facts: .seo/truth.md "Art and sculpture fabrication scope" and
// "Art-fabrication quote and handoff boundary" (no fixed sculpture price band,
// no museum-quality label, installation only when quoted), plus the 3D
// printing, modeling and scanning prices. Named artists are the three the
// truth row lists; other pieces are captioned without names.
const { pricing, reviewedDate, studios, photo } = require('./shared')

const photos = {
  austinLeeShow: photo(
    '6ouCyan5UEoxdvpI4oMWrQ',
    'f4e273f8bc1a032ed3c1909b7ab851d5/joshua-white-austin-lee-sculpture-min.jpg',
    2500,
    1672,
    'Colorful 3D printed figure sculptures by Austin Lee installed in a large gallery',
    'Sculptures for Austin Lee'
  ),
  austinLeeTall: photo(
    '33eadFRtkpbMjbkgLPcLWR',
    'e05fe6fb96f4929ee40bfdc9f739bf59/L1030812.jpg',
    3333,
    5000,
    'A 6-foot painted figure sculpture by Austin Lee with raised arms in a crowded gallery',
    '6-foot sculpture for Austin Lee',
    'A 6-foot figure, printed, assembled and painted.'
  ),
  austinLeeSmall: photo(
    '5jtfbg1QlP65JfTvHxWd8A',
    'a5abdd70eaa2dc9080b24cec4f1308a1/L1030802-2_copy.jpg',
    2000,
    2618,
    'A yellow figure sculpture by Austin Lee on a white plinth in a gallery',
    'Plinth-size piece for Austin Lee',
    'A smaller painted figure on a gallery plinth.'
  ),
  tinMan: photo(
    '7E2irwmYl2aLVRFMSmoxdk',
    '51352400686628e266bc28d65ac41703/IMG_7996.jpg',
    3024,
    4032,
    'A silver Tin Man sculpture for artist Coco Fusco on a stepped plinth in a gallery',
    'Tin Man for Coco Fusco',
    'A standing figure with a silver metallic finish.'
  ),
  lamson: photo(
    'XZbmecugKjar3C31uieDu',
    '82e28352c1d9dec8f2600a523f8536f1/_DSC9360.jpg',
    7360,
    4912,
    'A rose-gold organic sculpture for artist William Lamson on a white background',
    'Rose-gold form for William Lamson',
    'An organic form with a metallic rose-gold finish.'
  ),
  stack: photo(
    '182wbuL5kDowAmJ355qSWq',
    '1389bd2d49f836319dc170f7ca19967f/_DSC7636.jpg',
    2002,
    3000,
    'A glossy black sculpture of stacked rippled discs on a white background',
    'Stacked black sculpture',
    'Thin stacked rings that would be hard to carve by hand.'
  ),
  floatingChair: photo(
    '4YyqW0B6qxqPo2VbnYehYt',
    '6d5c80a9df428506b43ffce27d7bc839/LAL_6795.jpg',
    3000,
    4495,
    'A sculpture of a brown tufted armchair that appears to float above its plinth',
    'Floating chair',
    'A painted chair sculpture shown above its base.'
  ),
  platedChair: photo(
    '2HUFG8LJ63B1ovcJZWVNRD',
    '1d635bd233063cbafd6526027c743125/L1040158.jpg',
    3000,
    2000,
    'A chrome metal-plated sculptural chair on display at an art event',
    'Chrome-plated art furniture',
    'Art furniture printed, prepared and plated to a mirror finish.'
  ),
  emojiHands: photo(
    '4Vx4wxq27gJVfnWf98Oq1u',
    'ba6aee425c56f1b48e41676c29796c54/WechatIMG1652.jpeg',
    1440,
    1080,
    'Two chrome-plated 3D printed praying-hands sculptures photographed in a studio',
    'Chrome praying hands',
    'Printed in resin, then chrome plated.'
  ),
}

const page = {
  path: '/services/art-fabrication/',
  title: 'Art Fabrication NYC | Sculpture Fabrication for Artists',
  description:
    'Art fabrication in NYC for artists, galleries and brands: sculptures 3D modeled, scanned, printed, painted or metal plated in our Queens and Brooklyn studios.',
  heading: 'Art Fabrication and Sculpture Fabrication in NYC',
  eyebrow: 'Art fabricators in Queens & Brooklyn, New York',
  answer:
    'PrintAWorld is an art fabrication studio in Glendale, Queens and Brooklyn, New York. We turn sketches, photos, clay models, scans and 3D files into finished sculpture for artists, galleries, brands and agencies, using 3D modeling, 3D scanning, 3D printing, CNC milling, mold making, assembly, painting and metal plating. We’ve fabricated sculptures for Austin Lee, Coco Fusco and William Lamson, including a 6-foot figure. Every project is quoted after we review it; there’s no fixed sculpture price. Pick up by appointment, or we pack and ship across the U.S.',
  hero: 'austinLeeShow',
  reviewedDate,
  crumbs: [{ name: 'Art fabrication' }],
  service: {
    name: 'Art and Sculpture Fabrication in NYC',
    serviceType: 'Art and sculpture fabrication',
    areaServed: [
      { '@type': 'City', name: 'New York' },
      { '@type': 'Country', name: 'United States' },
    ],
    description:
      'Art and sculpture fabrication from New York studios: 3D modeling, scanning, printing, CNC milling, mold making, sectioning, assembly, painting and metal-plated finishing, quoted per project, with pickup by appointment or shipping across the U.S.',
  },
  quoteLabel: 'Start an art fabrication quote',
}

const intro = {
  eyebrow: 'Art fabrication NYC',
  heading: 'What is art fabrication?',
  paragraphs: [
    'Art fabrication is making an artist’s work when it’s too big, too complex or too time-consuming to build alone in the studio. The artist keeps the concept and the decisions; the fabricator works out how to build it, makes it and finishes it.',
    'Our part is digital fabrication with hands-on finishing. We model or scan your piece, print it in resin or on our FDM farm, mill or mold where it helps, then sand, paint or plate it so it reads as finished work. A 6-foot figure gets printed in sections and assembled; a small edition gets printed side by side.',
  ],
}

const glance = {
  heading: 'Our art and sculpture fabrication at a glance',
  rows: [
    ['Studios', studios],
    [
      'Who it’s for',
      'Artists, galleries, brands and agencies making sculpture, installations and art objects',
    ],
    [
      'Methods',
      '3D modeling, 3D scanning, 3D printing, CNC milling, mold making, sectioning, assembly',
    ],
    ['Finishes', 'Sanding, priming, painting, engraving and metal plating'],
    ['Starting points', 'Sketches, photos, clay models, scans or 3D files'],
    ['Largest size', '6 feet and more, printed in sections and assembled'],
    ['Named work', 'Sculptures for Austin Lee, Coco Fusco and William Lamson'],
    [
      'Price',
      `Quoted per project. 3D modeling from $${pricing.modelingFrom}, 3D scanning from $${pricing.scanningFrom}.`,
    ],
    [
      'How you get a price',
      'A written scope and quote after we review the piece',
    ],
    [
      'Delivery',
      'Pickup by appointment, or packed and shipped across the U.S.',
    ],
    [
      'Not included',
      'Installation and engineering, unless the quote lists them',
    ],
  ],
}

const work = {
  eyebrow: 'Selected work',
  heading: 'Sculpture fabrication for artists: selected work',
  photos: [
    'austinLeeTall',
    'tinMan',
    'lamson',
    'austinLeeSmall',
    'stack',
    'platedChair',
  ],
}

const offer = {
  eyebrow: 'What we do',
  heading: 'What can an art fabrication project include?',
  index: '02 / THE METHODS',
  items: [
    {
      label: 'F1',
      title: '3D modeling and scanning',
      body: `We sculpt the piece digitally from your references, or scan a maquette or clay model and clean the file. Modeling from $${pricing.modelingFrom}, scanning from $${pricing.scanningFrom}.`,
    },
    {
      label: 'F2',
      title: '3D printing at any scale',
      body: 'Fine-detail resin for small pieces and editions; our FDM farm and large-format printers for big work, printed in sections.',
    },
    {
      label: 'F3',
      title: 'CNC milling and mold making',
      body: 'Milled parts and molds where a print isn’t the best route, chosen in review and listed in the quote.',
    },
    {
      label: 'F4',
      title: 'Assembly and surface work',
      body: 'Joining sections, filling seams, sanding and priming so the piece reads as one object.',
    },
    {
      label: 'F5',
      title: 'Paint and metal plating',
      body: 'Hand painting in your colors, or chrome, gold and other metal finishes plated over the print.',
    },
    {
      label: 'F6',
      title: 'Packing and delivery',
      body: 'Packed for the trip, picked up by appointment or shipped to your studio, gallery or fair.',
    },
  ],
}

const contrast = {
  eyebrow: 'Where to start',
  heading: 'Can you fabricate a sculpture from a sketch, clay model or scan?',
  paragraphs: [
    'Yes. Send whatever you have: sketches, photos, a clay maquette, a scan or a 3D file. We model what’s missing, send it for your approval, then fabricate it at the size you want.',
    'Have a small maquette you want at 6 feet? We 3D scan it, scale it and print it in sections. Want the same piece in chrome or gold? We plate the print.',
  ],
  links: [
    ['3D scanning', '/services/3d-scanning/'],
    ['Metal plating', '/services/metal-plating/'],
  ],
}

const steps = {
  eyebrow: 'How it works',
  heading: 'How art fabrication with our NYC studio works',
  items: [
    {
      title: 'Share the concept',
      body: 'References or files, target size, finish, quantity, where it will show, budget range and deadline.',
    },
    {
      title: 'We review it',
      body: 'By phone, email or a studio appointment. We suggest a route and flag risks before quoting.',
    },
    {
      title: 'Approve the scope',
      body: 'The written quote lists methods, materials, finish, approval stages, price and schedule.',
    },
    {
      title: 'We fabricate and hand off',
      body: 'Model approval, production, finishing, then pickup or packed delivery as quoted.',
    },
  ],
}

const fit = {
  eyebrow: 'Is it the right fit?',
  heading: 'Are we the right art fabricator for your project?',
  good: [
    'Figurative and organic sculpture that starts as a sketch, model or scan',
    'Large pieces printed in sections, up to 6 feet and more',
    'Editions and multiples of the same piece',
    'Painted or metal-plated finishes over a printed form',
    'Gallery, fair and brand deadlines that need a clear schedule',
  ],
  other: [
    'Cast bronze or poured metal: a foundry is the right partner (our metal finishes are plated)',
    'Structural public art that needs stamped engineering drawings',
    'On-site installation, unless your quote lists it',
    'Clear, glass-look finishes, which we don’t offer',
  ],
}

const pricing_ = {
  eyebrow: 'Budgeting your piece',
  heading: 'How much does art fabrication cost?',
  lead: {
    label: 'Art fabrication is',
    value: 'Quoted per project',
    note: 'There’s no fixed sculpture price; size, method and finish set it.',
  },
  rowsHeading: 'Known starting points',
  rows: [
    ['3D modeling from references', `From $${pricing.modelingFrom}`],
    ['3D scanning a maquette or object', `From $${pricing.scanningFrom}`],
    ['3D printing from a ready file', `From about $${pricing.printFrom}`],
    ['Sections, assembly and seams', 'Quoted per project'],
    ['Painting or metal plating', 'Quoted per project'],
    ['Packing and shipping', 'Quoted to your ZIP code'],
  ],
  note: 'Send a budget range with your concept, and we’ll suggest what fits it.',
}

const logistics = {
  eyebrow: 'Pickup & shipping',
  heading: 'Where do we deliver finished artwork?',
  paragraphs: [
    'We fabricate in our New York studios. Pick up by appointment at the studio named in your quote, or we pack the piece for the trip and ship it across the U.S. to your studio, gallery or fair. Working from the West Coast? See [art fabrication for Los Angeles](/sculpture-art-fabrication-los-angeles/).',
  ],
  aside: {
    eyebrow: 'Have a show date?',
    title: 'Tell us the deadline first.',
    body: 'Timing depends on size, method and finish; metal plating takes the longest. Your written quote sets the approval stages and the date.',
  },
}

const related = {
  heading: 'More for artists and sculptors',
  links: [
    [
      'Art fabrication in Los Angeles',
      '/sculpture-art-fabrication-los-angeles/',
    ],
    ['3D scanning', '/services/3d-scanning/'],
    ['Metal plating', '/services/metal-plating/'],
    ['Large-scale 3D printing', '/large-scale-3d-printing-service/'],
  ],
}

const faqs = [
  {
    question: 'What is art fabrication?',
    answer:
      'Art fabrication is making an artist’s work when it’s too large, complex or time-consuming to build alone. The artist owns the concept and approves each stage; the fabricator plans the build, makes it and finishes it. PrintAWorld does it with 3D modeling, scanning, printing, CNC milling, mold making, assembly, painting and metal plating.',
  },
  {
    question: 'Where can I find an art fabricator in NYC?',
    answer:
      'PrintAWorld’s studios are at 74-12 88th Street in Glendale, Queens and 616 Berriman Street in Brooklyn. Visits are by appointment; send your concept first, and we’ll review it by phone or email before you bring anything in.',
  },
  {
    question: 'How much does art fabrication cost?',
    answer: `It’s quoted per project; there’s no fixed sculpture price. Size, method, materials, finish, assembly and packing set it. Known starting points: 3D modeling from $${pricing.modelingFrom}, 3D scanning from $${pricing.scanningFrom}, and 3D printing from about $${pricing.printFrom} for a ready file. Send a budget range and we’ll suggest a scope that fits.`,
  },
  {
    question: 'Which artists have you fabricated work for?',
    answer:
      'Sculptures for Austin Lee, Coco Fusco and William Lamson, among others, from plinth-size figures to a 6-foot figure.',
  },
  {
    question: 'Can you make a large sculpture?',
    answer:
      'Yes. Large pieces are printed in sections, joined, filled and finished; projects have reached 6 feet and more. The quote confirms how it splits, how it’s supported and how it’s packed.',
  },
  {
    question: 'Can you cast my sculpture in bronze?',
    answer:
      'We don’t pour bronze. We can print the piece and plate it in a metal finish, which adds a metal surface but doesn’t make it solid metal. For cast bronze, a foundry is the right partner; we can still model or print a pattern for them.',
  },
  {
    question: 'Do you install the work?',
    answer:
      'Installation isn’t included unless your quote lists it. Tell us the site, mounting and access at the start, and the written scope will say exactly what we deliver.',
  },
  {
    question: 'Can I visit the studio?',
    answer:
      'Yes, by appointment. Don’t bring or ship artwork until the written quote names the studio, the timing and the handling instructions.',
  },
  {
    question: 'How long does art fabrication take?',
    answer:
      'It depends on size, method and finish, and metal plating takes the longest. Give us the show or event date first; the written quote sets the approval stages and the delivery date.',
  },
]

const checklist = [
  'Sketches, photos, a scan or 3D file of the piece',
  'Target size, quantity or edition size',
  'Finish: raw, painted, or plated (chrome, gold…)',
  'Where it will show, and any handling or install needs',
  'Budget range, deadline and delivery ZIP code',
]

module.exports = {
  key: 'art',
  page,
  photos,
  intro,
  glance,
  work,
  offer,
  contrast,
  steps,
  fit,
  pricing: pricing_,
  logistics,
  related,
  faqs,
  faqHeading: 'What do artists ask about art fabrication?',
  checklist,
  quote: {
    eyebrow: 'Let’s make your piece',
    heading: ['Tell us about the work.', 'We’ll plan the build.'],
    body: 'Send what you have and your date. We review it, suggest a route and reply with a written scope and price.',
  },
}
